import React, { MouseEventHandler, ReactNode } from 'react';
import useAppContext from 'flow/hooks/useAppContext';
import { twMerge } from 'tailwind-merge';
import BackButton from '../ui-components/BackButton';

interface Props {
    header?: string;
    action?: ReactNode;
    onClickBack?: MouseEventHandler;
    footer?: ReactNode;
    className?: string;
    children: ReactNode;
    childrenClassName?: string;
    discardHeader?: boolean;
    mainId?: string;
    mainClassName?: string;
    useContactCardStyles?: boolean;
}

const Page = ({
    header,
    action = null,
    onClickBack,
    footer,
    className = '',
    children,
    childrenClassName,
    discardHeader,
    mainId,
    mainClassName,
    useContactCardStyles,
}: Props) => {
    const { i18next } = useAppContext();

    const noMargin = className.includes('no-main-margin');

    return (
        <div
            className={twMerge(
                'badge-bg flex w-full flex-col bg-white',
                className,
            )}
        >
            {!discardHeader ? (
                <header className="flex h-16 w-full items-center justify-center border-b px-4 font-bold">
                    <div className="flex flex-1 justify-start">
                        {onClickBack ? (
                            <BackButton onClick={onClickBack} />
                        ) : null}
                    </div>

                    {i18next.t(header)}

                    <div className="flex flex-1 justify-end">{action}</div>
                </header>
            ) : null}

            <main
                className={twMerge(
                    'scrollbar @container relative z-0 flex flex-1 overflow-y-scroll',
                    useContactCardStyles ? 'p-3 dark:p-0' : 'pt-6',
                    noMargin ? '' : 'mb-6',
                    mainClassName,
                )}
                id={mainId}
            >
                <div
                    className={twMerge(
                        // From @sm the column fills main until it is wider
                        // than max-w-screen-sm, so px-1 leaves room there
                        // for the focus ring of a full-width control, which
                        // main, the scroller, cuts off.
                        childrenClassName ||
                            'mx-auto w-full max-w-screen-sm p-3 @sm:px-1 @sm:py-0',
                    )}
                >
                    <div
                        className={twMerge(
                            'flex flex-1 flex-col pb-2',
                            className,
                        )}
                    >
                        {children}
                    </div>
                </div>
            </main>

            {footer ? (
                <footer className="flex h-16 items-center justify-end gap-x-3 border-t bg-gray-100 px-4 py-3">
                    {footer}
                </footer>
            ) : null}
        </div>
    );
};

export default Page;
